import { useState, useRef, useCallback, useEffect } from "react";
import type { TelemetryValue } from "../types/telemetry";
import type { AlertRule, AlertSeverity, FiredAlert } from "../types/alerts";
import { evaluateCondition, type TimestampedValue } from "../lib/alert-engine";
import { isStaleLiveTelemetryMetric } from "../lib/telemetryFreshness";
import { createAlertSounder, type AlertSounder } from "../lib/alertSound";

export interface ExternalAlert {
  ruleId: string;
  ruleName: string;
  severity: AlertSeverity;
  message: string;
  value?: number;
}

/** How long to keep samples in the rolling window (ms) */
const WINDOW_MS = 60_000;
/** How often to evaluate alert rules (ms) */
const EVAL_INTERVAL_MS = 1_000;
/** Maximum number of fired alerts to keep in history */
const MAX_HISTORY = 100;

const MIN_SAMPLE_COUNT_BY_METRIC = new Map<string, number>([
    ["car-pi/cpu_temp_c", 2],
    ["CONTROL_MODULE_VOLTAGE", 10],
]);

function hasEnoughSamplesForMetric(
  metric: string,
  samples: TimestampedValue[],
): boolean {
  const requiredSampleCount = MIN_SAMPLE_COUNT_BY_METRIC.get(metric) ?? 1;
  return samples.length >= requiredSampleCount;
}

export function useAlerts(
  rules: AlertRule[],
  metrics: Map<string, TelemetryValue>,
  publish?: (topic: string, payload: string) => boolean,
  topicPrefix?: string,
) {
  const [alerts, setAlerts] = useState<FiredAlert[]>([]);
  const [toasts, setToasts] = useState<FiredAlert[]>([]);
  const sounderRef = useRef<AlertSounder | null>(null);
  if (sounderRef.current === null) sounderRef.current = createAlertSounder();
  const [muted, setMutedState] = useState(() => sounderRef.current?.muted ?? false);

  // Rolling window of samples per metric
  const windowsRef = useRef<Map<string, TimestampedValue[]>>(new Map());
  // Last fire time per rule (for cooldown)
  const lastFiredRef = useRef<Map<string, number>>(new Map());
  // Keep latest metrics ref to avoid re-creating interval
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;
  const rulesRef = useRef(rules);
  rulesRef.current = rules;
  const publishRef = useRef(publish);
  publishRef.current = publish;
  const prefixRef = useRef(topicPrefix);
  prefixRef.current = topicPrefix;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const arm = () => sounderRef.current?.arm();
    window.addEventListener("pointerdown", arm, { once: true });
    window.addEventListener("keydown", arm, { once: true });
    return () => {
      window.removeEventListener("pointerdown", arm);
      window.removeEventListener("keydown", arm);
    };
  }, []);

  const fire = useCallback((alert: FiredAlert, ruleId: string, ruleName: string) => {
    sounderRef.current?.play(alert.severity);

    // Publish fired alert to MQTT for edge devices
    if (publishRef.current) {
      try {
        const prefix = prefixRef.current ?? "";
        const mqttPayload = JSON.stringify({
          ruleId,
          name: ruleName,
          severity: alert.severity,
          message: alert.message,
          value: alert.value,
          timestamp: alert.timestamp,
        });
        publishRef.current(
          `${prefix}alerts/${alert.severity}`,
          mqttPayload,
        );
      } catch {
        // Publish failed — alert still fires locally
      }
    }

    setAlerts((prev) => {
      const next = [alert, ...prev];
      return next.length > MAX_HISTORY
        ? next.slice(0, MAX_HISTORY)
        : next;
    });
    setToasts((prev) => [alert, ...prev]);
  }, []);

  // Update rolling windows whenever metrics change
  useEffect(() => {
    const now = Date.now();
    for (const [key, tv] of metrics) {
      if (isNaN(tv.value)) continue;
      const window = windowsRef.current.get(key) ?? [];
      window.push({ value: tv.value, timestamp: now });
      // Trim old samples
      const cutoff = now - WINDOW_MS;
      const trimmed = window.filter((s) => s.timestamp >= cutoff);
      windowsRef.current.set(key, trimmed);
    }
  }, [metrics]);

  // Periodic evaluation
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      const currentRules = rulesRef.current;
      const currentMetrics = metricsRef.current;

      for (const rule of currentRules) {
        if (!rule.enabled) continue;

        const tv = currentMetrics.get(rule.metric);
        if (!tv || isNaN(tv.value)) continue;
        if (isStaleLiveTelemetryMetric(rule.metric, tv, now)) continue;

        // Check cooldown
        const lastFired = lastFiredRef.current.get(rule.id) ?? 0;
        if (now - lastFired < rule.cooldownMs) continue;

        const samples = windowsRef.current.get(rule.metric) ?? [];
        if (!hasEnoughSamplesForMetric(rule.metric, samples)) continue;
        const fired = evaluateCondition(tv.value, samples, rule.condition);

        if (fired) {
          lastFiredRef.current.set(rule.id, now);
          fire(
            {
              id: `${rule.id}-${now}`,
              ruleId: rule.id,
              ruleName: rule.name,
              severity: rule.severity,
              message:
                rule.message ??
                `${rule.name}: ${tv.value.toFixed(1)}`,
              value: tv.value,
              timestamp: now,
              acknowledged: false,
            },
            rule.id,
            rule.name,
          );
        }
      }
    }, EVAL_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [fire]);

  const raise = useCallback(
    (external: ExternalAlert) => {
      const now = Date.now();
      fire(
        {
          id: `${external.ruleId}-${now}`,
          ruleId: external.ruleId,
          ruleName: external.ruleName,
          severity: external.severity,
          message: external.message,
          value: external.value ?? 0,
          timestamp: now,
          acknowledged: false,
        },
        external.ruleId,
        external.ruleName,
      );
    },
    [fire],
  );

  const setMuted = useCallback((next: boolean) => {
    sounderRef.current?.setMuted(next);
    setMutedState(next);
  }, []);

  const acknowledgeAlert = useCallback((alertId: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === alertId ? { ...a, acknowledged: true } : a)),
    );
  }, []);

  const dismissToast = useCallback((alertId: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== alertId));
  }, []);

  const clearAlerts = useCallback(() => {
    setAlerts([]);
  }, []);

  return {
    alerts,
    toasts,
    acknowledgeAlert,
    dismissToast,
    clearAlerts,
    raise,
    muted,
    setMuted,
  };
}
